import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Building2, Upload } from "lucide-react";
import { IMG } from "../data/site";
import { Seo, breadcrumbLd } from "../components/Seo";
import { PageHero, SectionHeading } from "../components/ui-blocks";

const targets = ["Builders", "General Contractors", "Remodelers", "Property Owners", "Commercial Developers"];
const applications = [
  { title: "Countertops & Surfaces", desc: "Durable stone surfaces for offices, break rooms and shared spaces." },
  { title: "Reception Areas", desc: "Statement stonework that sets the tone at the entrance." },
  { title: "Multifamily", desc: "Consistent fabrication and installation across units at scale." },
  { title: "Hospitality & Retail", desc: "Refined surfaces that stand up to high-traffic environments." },
  { title: "Offices", desc: "Professional stone applications for workspaces and common areas." },
  { title: "Custom Surfaces", desc: "Specialty fabrication tailored to your project specifications." },
];

const Commercial = () => {
  return (
    <div>
      <Seo
        title="Commercial Stone Solutions"
        description="Stone fabrication and installation for commercial projects — builders, contractors, property owners and developers. Request a commercial estimate."
        path="/commercial"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Commercial", path: "/commercial" }])}
      />
      <PageHero
        eyebrow="Commercial"
        title="Stone Solutions for Commercial Projects"
        subtitle="Professional fabrication and installation for qualifying contractors, builders, property owners and commercial applications."
        image={IMG.commercialCounter}
        testid="commercial-hero"
      />

      <section className="section bg-white">
        <div className="container-custom">
          <SectionHeading eyebrow="Who We Work With" title="Built for Professional Projects" className="mb-8" />
          <div className="flex flex-wrap gap-3 mb-16">
            {targets.map((t) => (
              <span key={t} className="px-4 py-2 rounded-full bg-[#FAFAF9] border border-neutral-200 text-sm font-medium text-neutral-700">
                {t}
              </span>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {applications.map((a) => (
              <div key={a.title} className="card-flat p-6">
                <Building2 size={22} className="text-[#CD1422] mb-4" />
                <h3 className="text-xl mb-2">{a.title}</h3>
                <p className="text-neutral-600 text-sm">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#1A3D84]">
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl text-white mb-4">Have Plans or Specifications?</h2>
            <p className="text-white/75 mb-6 leading-relaxed">
              Upload your plans or drawings with your estimate request and our team will review the details for your commercial project.
            </p>
            <ul className="space-y-3 mb-8">
              {["Secure file uploads (PDF & images)", "Reviewed by our team", "Fabrication to project specifications"].map((x) => (
                <li key={x} className="flex items-center gap-2.5 text-white/85 text-sm">
                  <Check size={16} className="text-[#CD1422]" /> {x}
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn btn-primary" data-testid="commercial-estimate-cta">
              <Upload size={17} /> Request Commercial Estimate
            </Link>
          </div>
          <div className="rounded-lg overflow-hidden img-zoom h-80">
            <img src={IMG.commercialLobby} alt="Commercial stone reception" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Commercial;
